'use client';

import { useId } from 'react';
import { DialogContent } from '@pixum/web-dialog';
import Text, { TEXT_VARIANT, TEXT_TAG } from '@pixum/text';
import styles from './UploadErrorDialogContent.module.css';

interface UploadErrorDialogContentProps {
  imageUrl?: string;
  title: string;
  titleMultiple?: string;
  contentText: string;
  contentTextMultiple?: string;
  files: File[];
  showFileList?: boolean;
  affectedFilesLabel?: string;
}

function ImageMaskWithStars({ className, idPrefix, src }: { className?: string; idPrefix: string; src: string }) {
  const maskId = `uem-${idPrefix.replace(/[^a-zA-Z0-9]/g, '')}-mask`;

  return (
    <svg
      className={className}
      fill="none"
      height={180}
      viewBox="0 0 205 205"
      width={180}
      xmlns="http://www.w3.org/2000/svg"
    >
      <svg data-testid="preview--image" height="100%" width="100%">
        <defs>
          <pattern height="100%" id={maskId} patternContentUnits="objectBoundingBox" width="100%">
            <image height="1" preserveAspectRatio="xMidYMid slice" width="1" href={src} />
          </pattern>
        </defs>
        <circle cx="50%" cy="50%" fill={`url(#${maskId})`} r="50%" />
      </svg>
      <path
        d="M54.117 28.0141C55.5573 28.0141 56.7249 26.8177 56.7249 25.3418C56.7249 23.8659 55.5573 22.6695 54.117 22.6695C52.6767 22.6695 51.5091 23.8659 51.5091 25.3418C51.5091 26.8177 52.6767 28.0141 54.117 28.0141Z"
        fill="#AAAAAA"
      />
      <path
        d="M17.9462 20.8408H19.3187C19.8678 20.8408 20.3482 20.3485 20.3482 19.7859V18.3795C20.3482 17.3949 21.1031 16.551 22.1325 16.551C23.0933 16.551 23.9169 17.3246 23.9169 18.3795V19.7859C23.9169 20.3485 24.3973 20.8408 24.9463 20.8408H26.3189C27.2797 20.8408 28.1032 21.6143 28.1032 22.6692C28.1032 23.6537 27.3483 24.4976 26.3189 24.4976H24.9463C24.3973 24.4976 23.9169 24.9899 23.9169 25.5525V26.959C23.9169 27.9435 23.1619 28.7874 22.1325 28.7874C21.1717 28.7874 20.3482 28.0138 20.3482 26.959V25.5525C20.3482 24.9899 19.8678 24.4976 19.3187 24.4976H17.9462C16.9854 24.4976 16.1618 23.7241 16.1618 22.6692C16.1618 21.6847 16.9854 20.8408 17.9462 20.8408Z"
        fill="#99DCFD"
      />
      <path
        d="M36.3926 9.41529C37.6434 9.41529 38.6574 8.37628 38.6574 7.09461C38.6574 5.81293 37.6434 4.77393 36.3926 4.77393C35.1418 4.77393 34.1279 5.81293 34.1279 7.09461C34.1279 8.37628 35.1418 9.41529 36.3926 9.41529Z"
        fill="#FF6600"
      />
      <path
        d="M168.94 204.999C166.777 204.999 164.984 203.164 164.984 200.951C164.984 198.737 166.777 196.902 168.94 196.902C171.102 196.902 172.895 198.737 172.895 200.951C172.895 203.164 171.155 204.999 168.94 204.999ZM168.94 198.467C167.568 198.467 166.461 199.601 166.461 201.005C166.461 202.408 167.568 203.542 168.94 203.542C170.311 203.542 171.419 202.408 171.419 201.005C171.419 199.601 170.311 198.467 168.94 198.467Z"
        fill="#AAAAAA"
      />
      <path
        d="M189.106 187.827H190.274C190.741 187.827 191.15 187.408 191.15 186.929V185.733C191.15 184.895 191.792 184.177 192.668 184.177C193.485 184.177 194.186 184.835 194.186 185.733V186.929C194.186 187.408 194.595 187.827 195.062 187.827H196.23C197.047 187.827 197.748 188.485 197.748 189.383C197.748 190.22 197.106 190.938 196.23 190.938H195.062C194.595 190.938 194.186 191.357 194.186 191.836V193.032C194.186 193.87 193.544 194.588 192.668 194.588C191.85 194.588 191.15 193.93 191.15 193.032V191.836C191.15 191.357 190.741 190.938 190.274 190.938H189.106C188.288 190.938 187.588 190.28 187.588 189.383C187.588 188.545 188.288 187.827 189.106 187.827Z"
        fill="#99DCFD"
      />
    </svg>
  );
}

export function UploadErrorDialogContent({
  imageUrl,
  title,
  titleMultiple,
  contentText,
  contentTextMultiple,
  files,
  showFileList = false,
  affectedFilesLabel
}: UploadErrorDialogContentProps) {
  const id = useId();
  const isMultiple = files.length > 1;
  const resolvedTitle = isMultiple && titleMultiple ? titleMultiple : title;
  const resolvedContent = isMultiple && contentTextMultiple ? contentTextMultiple : contentText;

  return (
    <DialogContent className={styles.dialogContent}>
      <>
        <Text tag={TEXT_TAG.DIV} className={styles.content}>
          {imageUrl && (
            <Text tag={TEXT_TAG.DIV} className={styles.imageContainer}>
              <ImageMaskWithStars idPrefix={id} src={imageUrl} />
            </Text>
          )}
          <Text tag={TEXT_TAG.DIV} className={styles.textContent}>
            <Text tag={TEXT_TAG.DIV} variant={TEXT_VARIANT.HEADLINE} dangerousHTML={{ __html: resolvedTitle }} />
            <Text tag={TEXT_TAG.DIV} variant={TEXT_VARIANT.CAPTION} dangerousHTML={{ __html: resolvedContent }} />
            {showFileList && files.length > 0 && (
              <Text tag={TEXT_TAG.DIV} variant={TEXT_VARIANT.CAPTION}>
                {affectedFilesLabel && (
                  <Text
                    tag={TEXT_TAG.DIV}
                    variant={TEXT_VARIANT.SUBHEADLINE}
                    dangerousHTML={{ __html: affectedFilesLabel }}
                  />
                )}
                {files.map(file => (
                  <Text key={file.name} tag={TEXT_TAG.DIV} variant={TEXT_VARIANT.CAPTION}>
                    {file.name}
                  </Text>
                ))}
              </Text>
            )}
          </Text>
        </Text>
        {imageUrl && <ImageMaskWithStars className={styles.imageMasks} idPrefix={`${id}-m`} src={imageUrl} />}
      </>
    </DialogContent>
  );
}
